/**
 * =============================================================================
 * formControls - Kit de controles de formulario DOM (skin dorado de Aureum)
 * =============================================================================
 *
 * Componentes reutilizables construidos a partir del arte de `assets/ui/form/`
 * (input, select, checkbox, radio). Mismo espíritu que `loadingBarDom.ts`: una
 * sola función `ensureFormControlsStyle()` inyecta el `<style>` compartido y
 * cada factory devuelve el HTMLElement listo para insertar, exponiendo el
 * `<input>/<select>` NATIVO real para que el código que migra siga leyendo
 * `.value` / `.checked` / `addEventListener('change')` igual que antes.
 *
 * REGLA: para cualquier input/select/checkbox/radio DOM nuevo, usar estas
 * factories — no crear `<input>`/`<select>` crudos con estilos ad-hoc. Ver
 * docs/UI_SKINNING.md §"Form controls (DOM)".
 *
 * Para scrollbars DOM ver `customScrollbar.ts`.
 */

import { getAssetUrl } from '@shared/constants';
import { playUiHover, playUiClick } from '../audio/uiSound';

// --- Assets (tamaños nativos medidos para los insets) ---------------------
const TEXT_INPUT_URL = getAssetUrl('ui/form/text_input.png'); // 552x92
const SELECT_BUTTON_URL = getAssetUrl('ui/form/select_dropdown_button.png'); // 60x60
const SELECT_BUTTON_HOVER_URL = getAssetUrl('ui/form/select_dropdown_button-HOVER.png');
const CHECKBOX_EMPTY_URL = getAssetUrl('ui/form/checkbox_empty.png'); // 58x58
const CHECKBOX_SELECTED_URL = getAssetUrl('ui/form/checkbox_selected.png');
const RADIO_EMPTY_URL = getAssetUrl('ui/form/radio_empty.png'); // 58x58
const RADIO_SELECTED_URL = getAssetUrl('ui/form/radio_selected.png');

/** Alto por defecto (px) de inputs/selects: deja respirar el marco del arte. */
const FIELD_HEIGHT = 38;
/** Lado (px) de la cajita de checkbox/radio. */
const BOX_SIZE = 22;
/** Color de texto dorado claro, coherente con el HUD. */
const TEXT_COLOR = '#e8dcc0';
const PLACEHOLDER_COLOR = '#8c8270';

let styleInjected = false;
let lpSuppressInstalled = false;

/**
 * Suprime el ícono/overlay de los gestores de contraseñas (LastPass inyecta
 * `<div data-lastpass-icon-root>` con `display:initial !important` INLINE, que un
 * stylesheet no puede pisar — hay que setear el display inline por JS). Sweep
 * inicial + MutationObserver para los que se inyectan al renderizar cada form.
 */
function suppressPasswordManagerIcons(): void {
  if (lpSuppressInstalled || typeof document === 'undefined' || !document.body) return;
  lpSuppressInstalled = true;

  const hideEl = (el: Element): void => {
    if (el instanceof HTMLElement) el.style.setProperty('display', 'none', 'important');
  };
  const sweep = (root: ParentNode): void => {
    root.querySelectorAll?.('[data-lastpass-icon-root]').forEach(hideEl);
  };
  sweep(document);

  const mo = new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (!(node instanceof HTMLElement)) continue;
        if (node.matches('[data-lastpass-icon-root]')) hideEl(node);
        sweep(node);
      }
    }
  });
  mo.observe(document.body, { childList: true, subtree: true });
}

/**
 * Inyecta (una sola vez) el `<style id="aureum-form-controls">` con todas las
 * reglas compartidas: marcos como background, swap checked/empty vía selector
 * de hermano, placeholder y focus.
 */
export function ensureFormControlsStyle(): void {
  // Instalar la supresión de íconos de gestores de contraseñas (idempotente).
  suppressPasswordManagerIcons();
  if (styleInjected || document.getElementById('aureum-form-controls')) {
    styleInjected = true;
    return;
  }
  const style = document.createElement('style');
  style.id = 'aureum-form-controls';
  style.textContent = `
    .aureum-field {
      position: relative;
      display: block;
      width: 100%;
      height: ${FIELD_HEIGHT}px;
      box-sizing: border-box;
      background-size: 100% 100%;
      background-repeat: no-repeat;
    }
    .aureum-field > input,
    .aureum-field > select {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      box-sizing: border-box;
      background: transparent;
      border: none;
      outline: none;
      color: ${TEXT_COLOR};
      font-family: inherit;
      font-size: 14px;
      padding: 0 12px;
      margin: 0;
    }
    .aureum-field > input::placeholder { color: ${PLACEHOLDER_COLOR}; }
    .aureum-field.aureum-focus { filter: brightness(1.12); }

    /* --- Select ----------------------------------------------------------
       Comparte el MISMO marco que el input de texto (text_input.png): ese arte
       tiene un borde claro y un ratio cercano al del campo, así que se ve igual
       que "Nombre". El select_dropdown_container.png tenía el borde demasiado
       sutil y quedaba plano. Encima va el botón de flecha integrado. */
    .aureum-field--select { background-image: url("${TEXT_INPUT_URL}"); }
    .aureum-field--select > select {
      -webkit-appearance: none;
      -moz-appearance: none;
      appearance: none;
      padding-left: 12px;
      padding-right: 32px; /* deja lugar al botón de flecha integrado */
      cursor: pointer;
      /* Tinta el resaltado de la opción en el popup nativo al dorado del tema
         (en vez del azul del SO). Chromium moderno lo respeta. */
      accent-color: #a87a32;
    }
    .aureum-field--select > select option {
      background: #20160f;
      color: ${TEXT_COLOR};
    }
    .aureum-field--select > select option:checked {
      background: #a87a32;
      color: #1a120a;
    }
    /* Botón de flecha: cuadrado, inset dentro del campo y centrado vertical,
       para que lea como botón integrado y no como baldosa pegada al borde. */
    .aureum-field__select-btn {
      position: absolute;
      top: 50%;
      right: 4px;
      transform: translateY(-50%);
      width: 22px;
      height: 22px;
      background-image: url("${SELECT_BUTTON_URL}");
      background-size: 100% 100%;
      background-repeat: no-repeat;
      pointer-events: none; /* el click lo maneja el <select> de abajo */
    }
    .aureum-field--select:hover .aureum-field__select-btn {
      background-image: url("${SELECT_BUTTON_HOVER_URL}");
    }

    /* --- Text input ------------------------------------------------------ */
    .aureum-field--text { background-image: url("${TEXT_INPUT_URL}"); }

    /* --- Checkbox / Radio ------------------------------------------------ */
    .aureum-check {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      cursor: pointer;
      color: ${TEXT_COLOR};
      font-family: inherit;
      font-size: 14px;
      user-select: none;
    }
    .aureum-check > input {
      position: absolute;
      opacity: 0;
      width: 0;
      height: 0;
      margin: 0;
      pointer-events: none;
    }
    .aureum-check__box {
      flex: 0 0 auto;
      width: ${BOX_SIZE}px;
      height: ${BOX_SIZE}px;
      background-size: 100% 100%;
      background-repeat: no-repeat;
    }
    .aureum-check--checkbox .aureum-check__box { background-image: url("${CHECKBOX_EMPTY_URL}"); }
    .aureum-check--checkbox > input:checked + .aureum-check__box {
      background-image: url("${CHECKBOX_SELECTED_URL}");
    }
    .aureum-check--radio .aureum-check__box { background-image: url("${RADIO_EMPTY_URL}"); }
    .aureum-check--radio > input:checked + .aureum-check__box {
      background-image: url("${RADIO_SELECTED_URL}");
    }
    .aureum-check > input:focus-visible + .aureum-check__box { filter: brightness(1.25); }

    /* --- Slider ---------------------------------------------------------- */
    .aureum-slider {
      position: relative;
      width: 100%;
      height: 22px;
      display: flex;
      align-items: center;
    }
    .aureum-slider__groove {
      position: absolute;
      left: 0; right: 0;
      top: 50%;
      transform: translateY(-50%);
      height: 6px;
      border-radius: 3px;
      background: linear-gradient(#1a1209, #0d0906);
      box-shadow: inset 0 1px 2px #000, 0 0 0 1px #3a2c18;
    }
    .aureum-slider__fill {
      position: absolute;
      left: 0; top: 0;
      height: 100%;
      border-radius: 3px 0 0 3px;
      background: linear-gradient(#caa15a, #7a5a23); /* dorado por defecto */
    }
    .aureum-slider__tick {
      position: absolute;
      top: 50%;
      transform: translate(-50%, -50%);
      width: 2px;
      height: 10px;
      background: #3a2c18;
      pointer-events: none;
    }
    .aureum-slider__handle {
      position: absolute;
      top: 50%;
      width: 18px;
      height: 18px;
      transform: translate(-50%, -50%);
      border-radius: 5px;
      /* Sin asset de handle aún: se dibuja un "gema" dorada por CSS. Cuando exista
         el PNG, basta con setear --aureum-slider-handle-img a una url(...). */
      background: var(--aureum-slider-handle-img,
        radial-gradient(circle at 35% 30%, #f2dca0, #b8893f 55%, #6e4e20));
      background-size: 100% 100%;
      background-repeat: no-repeat;
      border: 1px solid #2a1c0c;
      box-shadow: 0 1px 3px #000, inset 0 1px 1px rgba(255, 255, 255, 0.4);
      pointer-events: none;
    }
    .aureum-slider:hover .aureum-slider__handle,
    .aureum-slider__handle.aureum-dragging { filter: brightness(1.12); }
    .aureum-slider__input {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      margin: 0;
      opacity: 0;
      cursor: pointer;
      -webkit-appearance: none;
      appearance: none;
      background: transparent;
    }
    .aureum-slider-block { display: flex; flex-direction: column; gap: 2px; }
    .aureum-slider__labels {
      display: flex;
      justify-content: space-between;
      margin-top: 2px;
      font-size: 11px;
      color: ${PLACEHOLDER_COLOR};
    }

    /* --- Botones ---------------------------------------------------------
       Misma paleta dorada/marrón que los inputs. Primario: relleno dorado con
       texto oscuro (acción principal). Ghost: marrón oscuro con borde y texto
       dorado (acción secundaria). */
    .aureum-btn {
      display: inline-block;
      box-sizing: border-box;
      font-family: inherit;
      font-size: 14px;
      font-weight: 600;
      letter-spacing: 0.3px;
      color: #1a120a;
      background: linear-gradient(#d8b26a, #9a7330);
      border: 1px solid #6e4e20;
      border-radius: 4px;
      padding: 9px 14px;
      cursor: pointer;
      text-align: center;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.3);
      transition: filter 0.15s ease, transform 0.05s ease;
    }
    .aureum-btn:hover:not(:disabled) { filter: brightness(1.08); }
    .aureum-btn:active:not(:disabled) { transform: translateY(1px); }
    .aureum-btn:disabled { filter: grayscale(0.5) brightness(0.6); cursor: default; }
    .aureum-btn--ghost {
      color: ${TEXT_COLOR};
      background: linear-gradient(#2c2016, #1a1209);
      border: 1px solid #5a4426;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.06);
    }
    /* Variante "Entrar con Discord": hereda la profundidad de .aureum-btn (sombra,
       bisel, brillo al hover, hundido al :active) con la paleta blurple de Discord. */
    .aureum-btn--discord {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
      color: #ffffff;
      background: linear-gradient(#5d69f5, #4650cc);
      border: 1px solid #3c46b8;
    }
  `;
  document.head.appendChild(style);
  styleInjected = true;
}

export interface TextInputOptions {
  type?: 'text' | 'password' | 'email' | 'search';
  placeholder?: string;
  value?: string;
  id?: string;
  name?: string;
  autocomplete?: string;
}

export interface TextInputControl {
  /** Wrapper con el marco como background; insertar ESTE en el DOM. */
  wrapper: HTMLDivElement;
  /** `<input>` nativo real (leer `.value`, escuchar eventos). */
  input: HTMLInputElement;
}

/** Crea un input de texto enmarcado con `text_input.png`. */
export function createTextInput(opts: TextInputOptions = {}): TextInputControl {
  ensureFormControlsStyle();

  const wrapper = document.createElement('div');
  wrapper.className = 'aureum-field aureum-field--text';

  const input = document.createElement('input');
  input.type = opts.type ?? 'text';
  if (opts.placeholder !== undefined) input.placeholder = opts.placeholder;
  if (opts.value !== undefined) input.value = opts.value;
  if (opts.id !== undefined) input.id = opts.id;
  if (opts.name !== undefined) input.name = opts.name;
  if (opts.autocomplete !== undefined) {
    // `.autocomplete` está tipado como AutoFill (unión literal); setAttribute lo
    // refleja igual a la propiedad y acepta un string arbitrario.
    input.setAttribute('autocomplete', opts.autocomplete);
  }
  // Suprimir el ícono/sugerencia de los gestores de contraseñas (LastPass, 1Password,
  // Bitwarden, Dashlane, Proton Pass). `data-form-type="other"` es clave para que LastPass
  // NO trate el campo como login y deje de inyectar su overlay.
  input.setAttribute('data-lpignore', 'true');
  input.setAttribute('data-form-type', 'other');
  input.setAttribute('data-1p-ignore', 'true');
  input.setAttribute('data-bwignore', 'true');
  input.setAttribute('data-dashlane-rid', 'false');
  input.setAttribute('data-protonpass-ignore', 'true');

  input.addEventListener('focus', () => wrapper.classList.add('aureum-focus'));
  input.addEventListener('blur', () => wrapper.classList.remove('aureum-focus'));

  wrapper.appendChild(input);
  return { wrapper, input };
}

export interface ButtonOptions {
  /** 'primary' (dorado, default) o 'ghost' (marrón oscuro, acción secundaria). */
  variant?: 'primary' | 'ghost';
  /** Estira el botón al 100% del ancho del contenedor. */
  fullWidth?: boolean;
}

/**
 * Botón con el skin dorado/marrón de Aureum (clases `.aureum-btn`). Devuelve el
 * `<button>` nativo: enganchar `.onclick`, leer/setear `.disabled`, etc. igual.
 */
export function createButton(label: string, opts: ButtonOptions = {}): HTMLButtonElement {
  ensureFormControlsStyle();
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.textContent = label;
  btn.className = 'aureum-btn' + (opts.variant === 'ghost' ? ' aureum-btn--ghost' : '');
  if (opts.fullWidth) btn.style.width = '100%';
  // SFX de UI (hover/click). No-op hasta que haya AudioManager. Ver docs/SOUND_AND_MUSIC.md.
  btn.addEventListener('mouseenter', () => playUiHover());
  btn.addEventListener('click', () => playUiClick());
  return btn;
}

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectOptions {
  options?: SelectOption[];
  value?: string;
  id?: string;
  name?: string;
}

export interface SelectControl {
  wrapper: HTMLDivElement;
  /** `<select>` nativo real. */
  select: HTMLSelectElement;
  /** Reemplaza las `<option>`s del select. */
  setOptions: (options: SelectOption[]) => void;
}

/** Crea un select enmarcado con `select_dropdown_container.png` + botón de flecha. */
export function createSelect(opts: SelectOptions = {}): SelectControl {
  ensureFormControlsStyle();

  const wrapper = document.createElement('div');
  wrapper.className = 'aureum-field aureum-field--select';

  const select = document.createElement('select');
  if (opts.id !== undefined) select.id = opts.id;
  if (opts.name !== undefined) select.name = opts.name;

  const setOptions = (options: SelectOption[]): void => {
    select.innerHTML = '';
    for (const o of options) {
      const opt = document.createElement('option');
      opt.value = o.value;
      opt.textContent = o.label;
      select.appendChild(opt);
    }
  };
  if (opts.options) setOptions(opts.options);
  if (opts.value !== undefined) select.value = opts.value;

  select.addEventListener('focus', () => wrapper.classList.add('aureum-focus'));
  select.addEventListener('blur', () => wrapper.classList.remove('aureum-focus'));

  // Cajita de flecha (decorativa; el click lo captura el <select>).
  const arrow = document.createElement('div');
  arrow.className = 'aureum-field__select-btn';

  wrapper.appendChild(select);
  wrapper.appendChild(arrow);
  return { wrapper, select, setOptions };
}

export interface CheckOptions {
  label?: string;
  checked?: boolean;
  id?: string;
  name?: string;
  value?: string;
}

export interface CheckControl {
  /** `<label>` envolvente; insertar ESTE en el DOM. */
  wrapper: HTMLLabelElement;
  /** `<input>` nativo real (leer `.checked`). */
  input: HTMLInputElement;
}

function createCheck(kind: 'checkbox' | 'radio', opts: CheckOptions): CheckControl {
  ensureFormControlsStyle();

  const wrapper = document.createElement('label');
  wrapper.className = `aureum-check aureum-check--${kind}`;

  const input = document.createElement('input');
  input.type = kind;
  if (opts.checked) input.checked = true;
  if (opts.id !== undefined) input.id = opts.id;
  if (opts.name !== undefined) input.name = opts.name;
  if (opts.value !== undefined) input.value = opts.value;

  const box = document.createElement('span');
  box.className = 'aureum-check__box';

  wrapper.appendChild(input);
  wrapper.appendChild(box);

  if (opts.label !== undefined) {
    const text = document.createElement('span');
    text.textContent = opts.label;
    wrapper.appendChild(text);
  }

  return { wrapper, input };
}

/** Crea un checkbox con swap empty↔selected (`checkbox_*.png`). */
export function createCheckbox(opts: CheckOptions = {}): CheckControl {
  return createCheck('checkbox', opts);
}

/** Crea un radio con swap empty↔selected (`radio_*.png`). Agrupar por `name`. */
export function createRadio(opts: CheckOptions = {}): CheckControl {
  return createCheck('radio', opts);
}

export interface RadioGroupOptions {
  name: string;
  options: SelectOption[];
  value?: string;
}

export interface RadioGroupControl {
  /** Contenedor con todos los radios. */
  wrapper: HTMLDivElement;
  /** Devuelve el value seleccionado (o null). */
  getValue: () => string | null;
  /** Marca un value como seleccionado. */
  setValue: (value: string) => void;
  /** Los inputs nativos por value. */
  inputs: Map<string, HTMLInputElement>;
}

/** Crea un grupo de radios mutuamente excluyentes (mismo `name`). */
export function createRadioGroup(opts: RadioGroupOptions): RadioGroupControl {
  ensureFormControlsStyle();

  const wrapper = document.createElement('div');
  wrapper.style.display = 'flex';
  wrapper.style.flexDirection = 'column';
  wrapper.style.gap = '6px';

  const inputs = new Map<string, HTMLInputElement>();
  for (const o of opts.options) {
    const radio = createRadio({
      name: opts.name,
      label: o.label,
      value: o.value,
      checked: opts.value === o.value,
    });
    inputs.set(o.value, radio.input);
    wrapper.appendChild(radio.wrapper);
  }

  const getValue = (): string | null => {
    for (const [value, input] of inputs) {
      if (input.checked) return value;
    }
    return null;
  };
  const setValue = (value: string): void => {
    const input = inputs.get(value);
    if (input) input.checked = true;
  };

  return { wrapper, getValue, setValue, inputs };
}

export interface SliderOptions {
  min?: number;
  max?: number;
  step?: number;
  value?: number;
  id?: string;
  name?: string;
  /** Color/gradiente CSS del fill (ej. '#c0392b' para el rojo de la referencia). */
  fillColor?: string;
  /** Background CSS custom de la pista (ej. un gradiente día/noche). */
  grooveBackground?: string;
  /** Oculta el fill de progreso (para sliders-selector donde la pista YA es informativa). */
  hideFill?: boolean;
  /**
   * Modo escalonado: etiquetas equiespaciadas (LOW/MEDIUM/HIGH/...). Fija
   * min=0, max=labels.length-1, step=1 y dibuja ticks + fila de labels debajo.
   */
  labels?: string[];
  /** Callback en cada cambio de valor. */
  onChange?: (value: number) => void;
}

export interface SliderControl {
  /** Elemento a insertar (incluye barra y, si hay, la fila de labels). */
  wrapper: HTMLDivElement;
  /** Sólo la barra (groove+fill+handle+input), por si se quiere `flex:1`. */
  bar: HTMLDivElement;
  /** `<input type=range>` nativo real. */
  input: HTMLInputElement;
  getValue: () => number;
  setValue: (value: number) => void;
}

/**
 * Slider dorado con groove + fill + handle (gema CSS, swap-ready). Soporta modo
 * continuo o escalonado (`labels`). El `<input type=range>` nativo va transparente
 * por encima → conserva teclado/drag/accesibilidad. Ver docs/UI_SKINNING.md.
 */
export function createSlider(opts: SliderOptions = {}): SliderControl {
  ensureFormControlsStyle();

  const stepped = opts.labels !== undefined && opts.labels.length > 0;
  const min = stepped ? 0 : (opts.min ?? 0);
  const max = stepped ? opts.labels!.length - 1 : (opts.max ?? 100);
  const step = stepped ? 1 : (opts.step ?? 1);
  const value = opts.value ?? min;

  const wrapper = document.createElement('div');
  wrapper.className = 'aureum-slider-block';

  const bar = document.createElement('div');
  bar.className = 'aureum-slider';

  const groove = document.createElement('div');
  groove.className = 'aureum-slider__groove';
  if (opts.grooveBackground) groove.style.background = opts.grooveBackground;

  const fill = document.createElement('div');
  fill.className = 'aureum-slider__fill';
  if (opts.fillColor) fill.style.background = opts.fillColor;
  if (opts.hideFill) fill.style.display = 'none';
  if (!opts.hideFill) groove.appendChild(fill);

  // Ticks (sólo en modo escalonado).
  if (stepped && opts.labels!.length > 1) {
    for (let i = 0; i < opts.labels!.length; i++) {
      const tick = document.createElement('div');
      tick.className = 'aureum-slider__tick';
      tick.style.left = `${(i / (opts.labels!.length - 1)) * 100}%`;
      groove.appendChild(tick);
    }
  }

  const handle = document.createElement('div');
  handle.className = 'aureum-slider__handle';

  const input = document.createElement('input');
  input.className = 'aureum-slider__input';
  input.type = 'range';
  input.min = String(min);
  input.max = String(max);
  input.step = String(step);
  input.value = String(value);
  if (opts.id !== undefined) input.id = opts.id;
  if (opts.name !== undefined) input.name = opts.name;

  bar.append(groove, handle, input);

  const sync = (): void => {
    const v = parseFloat(input.value);
    const pct = max > min ? ((v - min) / (max - min)) * 100 : 0;
    fill.style.width = `${pct}%`;
    handle.style.left = `${pct}%`;
  };
  input.addEventListener('input', () => {
    sync();
    if (opts.onChange) opts.onChange(parseFloat(input.value));
  });
  input.addEventListener('pointerdown', () => handle.classList.add('aureum-dragging'));
  window.addEventListener('pointerup', () => handle.classList.remove('aureum-dragging'));

  wrapper.appendChild(bar);

  if (stepped) {
    const labelsRow = document.createElement('div');
    labelsRow.className = 'aureum-slider__labels';
    for (const label of opts.labels!) {
      const span = document.createElement('span');
      span.textContent = label;
      labelsRow.appendChild(span);
    }
    wrapper.appendChild(labelsRow);
  }

  // El fill/handle dependen del ancho renderizado: sync inicial y al primer layout.
  sync();
  requestAnimationFrame(sync);

  const getValue = (): number => parseFloat(input.value);
  const setValue = (v: number): void => {
    input.value = String(v);
    sync();
  };

  return { wrapper, bar, input, getValue, setValue };
}
